import React, { useState, useEffect, useRef } from 'react';
import './GraficoPedidos.css';
import { Chart } from 'primereact/chart';
import baseURL from '../../url';
import moneda from '../../moneda';
import { useTheme } from '../../ThemeContext/ThemeContext';

export default function GraficoProductos() {
    const { mode } = useTheme();
    const [pedidos, setPedidos] = useState([]);
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});
    const pedidosRaw = useRef([]);

    useEffect(() => {
        cargarPedidos();
    }, []);

    useEffect(() => {
        if (pedidosRaw.current.length > 0) {
            procesarDatosGrafico(pedidosRaw.current);
        } else {
            establecerDatosPorDefecto();
        }
    }, [mode]);

    const cargarPedidos = () => {
        fetch(`${baseURL}/pedidoGet.php`, {
            method: 'GET',
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Error en la conexión');
                }
                return response.json();
            })
            .then(data => {
                // Filtra los pedidos pagados
                const pedidosFiltrados = data.pedidos.filter(pedido => pedido.pagado === 'Si');
                setPedidos(pedidosFiltrados?.reverse());
                pedidosRaw.current = pedidosFiltrados;
                procesarDatosGrafico(pedidosFiltrados);
            })
            .catch(error => {
                console.error('Error al cargar pedidos:', error);
                // Establecer datos por defecto en caso de error o sin pedidos
                establecerDatosPorDefecto();
            });
    };

    const procesarDatosGrafico = (pedidos) => {
        const contadorProductos = {};

        pedidos.forEach(pedido => {
            JSON.parse(pedido.productos).forEach(producto => {
                if (contadorProductos[producto.titulo]) {
                    contadorProductos[producto.titulo] += producto.cantidad; // Sumar cantidades
                } else {
                    contadorProductos[producto.titulo] = producto.cantidad; // Inicializar cantidad
                }
            });
        });

        // Filtrar y ordenar los productos más vendidos (por ejemplo, más de 2 ventas)
        const productosFiltrados = Object.keys(contadorProductos)
            .filter(titulo => contadorProductos[titulo] > 3)
            .map(titulo => ({
                titulo,
                cantidad: contadorProductos[titulo]
            }))
            .sort((a, b) => b.cantidad - a.cantidad) // Ordenar por cantidad de mayor a menor
            .slice(0, 6); // Limitar a los 6 productos más vendidos

        // Crear datos para el gráfico
        let labels, data;

        if (productosFiltrados.length > 0) {
            labels = productosFiltrados.map(producto => producto.titulo);
            data = productosFiltrados.map(producto => producto.cantidad);
        } else {
            establecerDatosPorDefecto();
            return; // Salir de la función para no establecer chartData con valores vacíos
        }

        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color3');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
        const color1 = documentStyle.getPropertyValue('--color1').trim() || '#6366f1';

        const backgroundColors = generateColorShades(color1, productosFiltrados.length);

        setChartData({
            labels: labels,
            datasets: [{
                label: 'Cantidad de productos vendidos',
                data: data,
                backgroundColor: backgroundColors,
                borderColor: color1,
                fill: true
            }]
        });

        setChartOptions({
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    grid: {
                        color: surfaceBorder
                    }
                }
            }
        });
    };


    const generateColorShades = (baseColor, count) => {
        const hex = baseColor.startsWith('#') ? baseColor : `#${baseColor}`;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);

        const shades = [];
        for (let i = 0; i < count; i++) {
            // Asignar opacidad total al más vendido (índice 0) y decrecer para el resto
            const alpha = i === 0 ? 0.9 : (0.5 - ((i - 1) * (0.6 / (count - 1)))); // Decrementar alpha
            shades.push(`rgba(${r}, ${g}, ${b}, ${alpha < 0.1 ? 0.1 : alpha})`); // Asegurarse de que no baje de 0.1
        }
        return shades; // Dejar el array en el orden original
    };

    const hexToRgb = (hex) => {
        if (!hex) return null;
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const establecerDatosPorDefecto = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color3');
        const color1 = documentStyle.getPropertyValue('--color1').trim() || '#6366f1';
        const colorRgb = hexToRgb(color1);
        const labels = ['Producto A', 'Producto B', 'Producto C'];
        const data = [5, 3, 8];

        setChartData({
            labels: labels,
            datasets: [{
                label: 'Cantidad de productos vendidos',
                data: data,
                backgroundColor: colorRgb ? `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.4)` : 'rgba(99, 102, 241, 0.4)',
                borderColor: color1 || '#6366f1',
                fill: true
            }]
        });

        setChartOptions({
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                }
            }
        });
    };

    return (
        <div className="GraficoContent">
            <h3 className='titleGrafico'>Productos más vendidos</h3>
            <div className="grafico-container-2">
                <Chart type="polarArea" data={chartData} options={chartOptions} />
            </div>
        </div>
    );
}
